import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from '../../../../shared/ui/Primitives';
import {
  RegionalRequestError,
  regionalErrorMessage,
} from '../../adapters/regional-monitoring-http';
import {
  actionLabels,
  actionNeedsEvidence,
  formatRegionalTime,
  localDateTimeToUtc,
  REGIONAL_STATUSES,
  statusLabels,
  validateRegionalAction,
  type CaseActionCommand,
  type ManualCaseCommand,
  type RegionalAction,
  type RegionalCell,
  type RegionalDetail,
  type RegionalOverview,
  type RegionalWriteResult,
} from '../../consumer/regional-monitoring';

function RegionalDialog({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: ReactNode;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialog.current?.showModal();
    return () => {
      dialog.current?.close();
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="regional-dialog"
      aria-labelledby="regional-dialog-title"
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <header className="regional-dialog-heading">
        <h2 id="regional-dialog-title">{title}</h2>
        <button
          type="button"
          className="regional-icon-button"
          aria-label="关闭详情"
          onClick={close}
        >
          <Icon name="close" />
        </button>
      </header>
      {children}
    </dialog>
  );
}
export function RegionalCaseDialog({
  detail,
  loading,
  error,
  close,
  reload,
  act,
}: {
  detail: RegionalDetail | null;
  loading: boolean;
  error: string;
  close: () => void;
  reload: () => void;
  act: (command: CaseActionCommand) => Promise<RegionalWriteResult>;
}) {
  const [busy, setBusy] = useState(false);
  const item = detail?.item;
  return (
    <RegionalDialog
      title={item?.title ?? '关注事项'}
      close={() => {
        if (!busy) close();
      }}
    >
      {loading && <p role="status">正在读取数据库中的事项与操作记录…</p>}
      {error && (
        <div className="regional-alert" role="alert">
          {error}
          <button className="regional-button" onClick={reload}>
            重新读取
          </button>
        </div>
      )}
      {item && (
        <>
          <div className="regional-case-context">
            <span className={`regional-status state-${item.status}`}>
              {statusLabels[item.status]}
            </span>
            <span>{item.origin === 'THRESHOLD' ? '自动阈值发现' : '主动发起核查'}</span>
            {item.isOverdue && <strong className="regional-overdue">已超过复核时间</strong>}
          </div>
          <ol className="regional-progress" aria-label="处理进度">
            {REGIONAL_STATUSES.map((state, i) => (
              <li
                key={state}
                aria-current={state === item.status ? 'step' : undefined}
                className={
                  state === item.status
                    ? 'is-current'
                    : REGIONAL_STATUSES.indexOf(item.status) > i
                      ? 'is-previous'
                      : ''
                }
              >
                <span>{i + 1}</span>
                {
                  [
                    '发现',
                    '核查',
                    item.disposition === 'NOT_CONFIRMED' ? '未证实' : '处理',
                    '复核',
                    '完成',
                  ][i]
                }
              </li>
            ))}
          </ol>
          <p className="regional-description">{item.description}</p>
          <dl className="regional-case-facts">
            <div>
              <dt>位置</dt>
              <dd>
                {item.latitude.toFixed(4)}°N，{item.longitude.toFixed(4)}°E
              </dd>
            </div>
            <div>
              <dt>责任记录</dt>
              <dd>{item.assignee || '尚未填写'}</dd>
            </div>
            <div>
              <dt>复核时间</dt>
              <dd>{item.dueAt ? `${formatRegionalTime(item.dueAt)} 北京时间` : '未设置'}</dd>
            </div>
            <div>
              <dt>发现时间</dt>
              <dd>{formatRegionalTime(item.openedAt)}</dd>
            </div>
          </dl>
          <details className="regional-evidence">
            <summary>发现依据与来源</summary>
            <p>{item.evidence.summary}</p>
            <dl>
              <dt>来源快照</dt>
              <dd>{item.evidence.snapshotId}</dd>
              <dt>预报有效时段</dt>
              <dd>
                {item.validFrom
                  ? `${formatRegionalTime(item.validFrom)} — ${formatRegionalTime(item.validTo)}`
                  : '人工核查引用的天气快照'}
              </dd>
              <dt>证据摘要 SHA-256</dt>
              <dd>
                <code>{item.evidence.rawSha256}</code>
              </dd>
              <dt>规则版本</dt>
              <dd>{item.evidence.ruleVersion ?? '人工发起，未声称阈值命中'}</dd>
            </dl>
          </details>
          {item.allowedActions.length > 0 && (
            <ActionForm
              key={`${item.id}:${item.revision}`}
              detail={detail}
              act={act}
              busy={busy}
              setBusy={setBusy}
              reload={reload}
            />
          )}
          <section className="regional-history">
            <h3>
              操作记录 <span>{detail.events.length}</span>
            </h3>
            <ol>
              {detail.events.map((event) => (
                <li key={event.id}>
                  <div>
                    <strong>
                      {actionLabels[event.action as RegionalAction] ??
                        (event.fromStatus === null ? '发现并建立事项' : event.action)}
                    </strong>
                    <time>{formatRegionalTime(event.occurredAt)}</time>
                  </div>
                  <p>{event.note || '已记录此步骤'}</p>
                  {event.evidenceNote && (
                    <p className="regional-muted">证据：{event.evidenceNote}</p>
                  )}
                  <small>
                    {event.actorLabel} · 记录版本 {event.revision}
                  </small>
                </li>
              ))}
            </ol>
          </section>
          <p className="regional-footnote">
            完成表示本次内部核查记录已闭合；不解除官方预警，不代表区域安全。当前复核操作者如实记录。
          </p>
        </>
      )}
    </RegionalDialog>
  );
}
function ActionForm({
  detail,
  act,
  busy,
  setBusy,
  reload,
}: {
  detail: RegionalDetail;
  act: (command: CaseActionCommand) => Promise<RegionalWriteResult>;
  busy: boolean;
  setBusy: (value: boolean) => void;
  reload: () => void;
}) {
  const [action, setAction] = useState<RegionalAction>(
    detail.item.allowedActions[0] ?? 'START_VERIFICATION',
  );
  const [note, setNote] = useState(''),
    [evidence, setEvidence] = useState(''),
    [assignee, setAssignee] = useState(detail.item.assignee ?? ''),
    [dueAt, setDueAt] = useState('');
  const [error, setError] = useState(''),
    [conflict, setConflict] = useState(false);
  const [pending, setPending] = useState<CaseActionCommand | null>(null);
  const submit = async () => {
    const invalid = validateRegionalAction(action, note, evidence);
    if (!pending && invalid) {
      setError(invalid);
      return;
    }
    const due = localDateTimeToUtc(dueAt);
    if (
      !pending &&
      dueAt &&
      (!due || Date.parse(due) <= Date.now() || Date.parse(due) > Date.now() + 90 * 86400000)
    ) {
      setError('复核时间应在未来 90 天内，按北京时间填写。');
      return;
    }
    const command: CaseActionCommand = pending ?? {
      requestId: crypto.randomUUID(),
      expectedRevision: detail.item.revision,
      action,
      note: note.trim(),
      ...(evidence.trim() ? { evidenceNote: evidence.trim() } : {}),
      ...(assignee.trim() ? { assignee: assignee.trim() } : {}),
      ...(due ? { dueAt: due } : {}),
    };
    setPending(command);
    setBusy(true);
    setError('');
    try {
      await act(command);
    } catch (failure) {
      setError(regionalErrorMessage(failure));
      setConflict(failure instanceof RegionalRequestError && failure.status === 409);
      if (failure instanceof RegionalRequestError && !failure.retryable && failure.status !== 401)
        setPending(null);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="regional-action-form">
      <h3>下一步 · {statusLabels[detail.item.status]}</h3>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <fieldset disabled={busy || pending !== null}>
          <legend className="regional-sr-only">当前操作内容</legend>
          <label>
            操作
            <select
              value={action}
              onChange={(event) => setAction(event.target.value as RegionalAction)}
            >
              {detail.item.allowedActions.map((value) => (
                <option key={value} value={value}>
                  {actionLabels[value]}
                </option>
              ))}
            </select>
          </label>
          <label>
            {action === 'SUBMIT_HANDLING'
              ? '处理结果'
              : action.startsWith('REVIEW')
                ? '复核结论'
                : '核查说明'}
            {action !== 'START_VERIFICATION' && '（必填）'}
            <textarea
              rows={3}
              maxLength={4000}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={
                action === 'START_VERIFICATION'
                  ? '可记录核查计划或检查范围'
                  : '记录核查事实、采取的措施或退回原因'
              }
              required={action !== 'START_VERIFICATION'}
              minLength={action === 'START_VERIFICATION' ? undefined : 5}
            />
          </label>
          {action !== 'START_VERIFICATION' && (
            <label>
              证据说明
              {actionNeedsEvidence(action)
                ? '（必填，至少 5 个字）'
                : '（选填，填写时至少 5 个字）'}
              <textarea
                rows={2}
                maxLength={4000}
                value={evidence}
                onChange={(event) => setEvidence(event.target.value)}
                placeholder="例如检查记录、现场反馈、核验时间及结果；填写文字说明"
                required={actionNeedsEvidence(action)}
                minLength={5}
              />
            </label>
          )}
          <div className="regional-form-row">
            <label>
              责任人记录
              <input
                value={assignee}
                maxLength={120}
                onChange={(event) => setAssignee(event.target.value)}
                placeholder="姓名或值守岗位"
              />
            </label>
            <label>
              下次复核时间（北京时间）
              <input
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </label>
          </div>
        </fieldset>
        {error && (
          <div className="regional-alert" role="alert">
            {error}
            {conflict && (
              <button type="button" className="regional-button" onClick={reload}>
                读取最新记录
              </button>
            )}
          </div>
        )}
        <div className="regional-form-footer">
          <small>操作记录由本地工作会话写入数据库</small>
          <button className="regional-button primary" disabled={busy || conflict} type="submit">
            {busy ? '正在保存…' : pending ? '重试同一提交' : actionLabels[action]}
          </button>
        </div>
      </form>
    </section>
  );
}
export function RegionalManualDialog({
  overview,
  cell,
  close,
  create,
}: {
  overview: RegionalOverview;
  cell: RegionalCell;
  close: () => void;
  create: (command: ManualCaseCommand) => Promise<void>;
}) {
  const [title, setTitle] = useState(
      `采样点 ${cell.requestedPoints[0]?.id.toUpperCase() ?? ''} 核查`,
    ),
    [note, setNote] = useState(''),
    [assignee, setAssignee] = useState(''),
    [dueAt, setDueAt] = useState('');
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [pending, setPending] = useState<ManualCaseCommand | null>(null);
  async function submit() {
    if (!overview.snapshot) return;
    if (!pending && (title.trim().length < 2 || note.trim().length < 5)) {
      setError('请填写标题及至少 5 个字的核查原因。');
      return;
    }
    const due = localDateTimeToUtc(dueAt);
    if (
      !pending &&
      dueAt &&
      (!due || Date.parse(due) <= Date.now() || Date.parse(due) > Date.now() + 90 * 86400000)
    ) {
      setError('复核时间应在未来 90 天内，按北京时间填写。');
      return;
    }
    const command = pending ?? {
      requestId: crypto.randomUUID(),
      snapshotId: overview.snapshot.id,
      gridId: cell.id,
      title: title.trim(),
      note: note.trim(),
      ...(assignee.trim() ? { assignee: assignee.trim() } : {}),
      ...(due ? { dueAt: due } : {}),
    };
    setPending(command);
    setBusy(true);
    setError('');
    try {
      await create(command);
    } catch (failure) {
      setError(regionalErrorMessage(failure));
      if (failure instanceof RegionalRequestError && !failure.retryable && failure.status !== 401)
        setPending(null);
    } finally {
      setBusy(false);
    }
  }
  return (
    <RegionalDialog
      title="主动发起核查"
      close={() => {
        if (!busy) close();
      }}
    >
      <p>为选中位置登记需要进一步核查的事项。此记录标为人工发起，不作为自动阈值异常。</p>
      <p className="regional-muted">
        {cell.latitude.toFixed(4)}°N，{cell.longitude.toFixed(4)}°E · 引用{' '}
        {formatRegionalTime(overview.source.fetchedAt)} 采集的真实天气快照
      </p>
      <form
        className="regional-manual-form"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <fieldset disabled={busy || pending !== null}>
          <legend className="regional-sr-only">核查事项</legend>
          <label>
            事项标题
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              minLength={2}
              maxLength={120}
              required
            />
          </label>
          <label>
            核查原因（必填）
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={4}
              minLength={5}
              maxLength={4000}
              required
              placeholder="请描述实际收到的信息、关注原因及需要核对的情况"
            />
          </label>
          <div className="regional-form-row">
            <label>
              责任人记录
              <input
                value={assignee}
                onChange={(event) => setAssignee(event.target.value)}
                maxLength={120}
                placeholder="姓名或值守岗位"
              />
            </label>
            <label>
              复核时间（北京时间）
              <input
                type="datetime-local"
                value={dueAt}
                onChange={(event) => setDueAt(event.target.value)}
              />
            </label>
          </div>
          <p className="regional-muted">未设置复核时间时，系统默认提醒在 24 小时内跟进。</p>
        </fieldset>
        {error && (
          <p className="regional-alert" role="alert">
            {error}
          </p>
        )}
        <div className="regional-form-footer">
          <small>真实快照与操作记录将持久保存</small>
          <button type="submit" className="regional-button primary" disabled={busy}>
            {busy ? '正在建立事项…' : pending ? '重试同一提交' : '发起核查'}
          </button>
        </div>
      </form>
    </RegionalDialog>
  );
}
